import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await axios.post(`${API_URL}/admin/login`, { email, password });
      if (res.data.success) {
        onLogin(res.data.token);
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError('Login failed. Please check your credentials.');
    }
    setLoading(false);
  };

  return (
    <div className="admin-login">
      <h1>Admin Login</h1>
      <form onSubmit={handleLogin}>
        <input 
          type="email" 
          placeholder="Email" 
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input 
          type="password" 
          placeholder="Password" 
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </button>
        {error && <p className="error-text">{error}</p>}
      </form>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = ({ token, onLogout }) => {
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState(null);
  const [sources, setSources] = useState(null);
  const [payments, setPayments] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: API_URL,
    headers: { 'X-Admin-Token': token }
  });

  const loadDashboard = useCallback(async () => {
    try {
      const res = await api.get('/admin/dashboard');
      setDashboard(res.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
    setLoading(false);
  }, [api, onLogout]);

  const loadUsers = useCallback(async () => {
    const res = await api.get('/admin/users');
    setUsers(res.data);
  }, [api]);

  const loadSources = useCallback(async () => {
    const res = await api.get('/admin/sources');
    setSources(res.data);
  }, [api]);

  const loadPayments = useCallback(async () => {
    const res = await api.get('/admin/payments');
    setPayments(res.data);
  }, [api]);

  const loadPipelineStatus = useCallback(async () => {
    const res = await api.get('/admin/pipeline/status');
    setPipelineStatus(res.data);
  }, [api]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const runPipeline = async () => {
    try {
      await api.post('/admin/pipeline/run');
      alert('Pipeline started!');
      loadPipelineStatus();
    } catch (err) {
      alert('Pipeline failed: ' + err.message);
    }
  };

  const toggleSource = async (sourceId, active) => {
    await api.put('/admin/sources/toggle', { source_id: sourceId, active });
    loadSources();
  };

  useEffect(() => {
    if (activeTab === 'users' && !users) loadUsers();
    if (activeTab === 'sources' && !sources) loadSources();
    if (activeTab === 'payments' && !payments) loadPayments();
    if (activeTab === 'pipeline' && !pipelineStatus) loadPipelineStatus();
  }, [activeTab, users, sources, payments, pipelineStatus, loadUsers, loadSources, loadPayments, loadPipelineStatus]);

  if (loading) return <div className="loading"><p>Loading admin dashboard...</p></div>;

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Framecast Admin</h1>
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </div>
      
      <div className="admin-tabs">
        {['overview', 'users', 'sources', 'payments', 'pipeline'].map(tab => (
          <button 
            key={tab}
            className={activeTab === tab ? 'active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      
      <div className="admin-content">
        {activeTab === 'overview' && dashboard && (
          <div className="overview-grid">
            <div className="stat-card">
              <h3>Content</h3>
              <div className="stat-value">{dashboard.content_stats.total_articles}</div>
              <div className="stat-label">Articles</div>
              <div className="stat-sub">{dashboard.content_stats.total_clusters} clusters</div>
            </div>
            <div className="stat-card">
              <h3>Users</h3>
              <div className="stat-value">{dashboard.user_stats.total_users}</div>
              <div className="stat-label">Total Users</div>
              <div className="stat-sub">{dashboard.user_stats.pro_users} pro</div>
            </div>
            <div className="stat-card">
              <h3>Revenue</h3>
              <div className="stat-value">${dashboard.revenue_stats.total_revenue}</div>
              <div className="stat-label">Total</div>
              <div className="stat-sub">{dashboard.revenue_stats.total_transactions} transactions</div>
            </div>
            <div className="stat-card">
              <h3>Digest</h3>
              <div className="stat-value">{dashboard.user_stats.digest_subscribers}</div>
              <div className="stat-label">Subscribers</div>
            </div>
          </div>
        )}
        
        {activeTab === 'users' && users && (
          <div className="admin-table">
            <h2>Users ({users.total})</h2>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.users.map(u => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td><span className={`tier-badge ${u.tier}`}>{u.tier}</span></td>
                    <td>{u.subscription_status || '-'}</td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'sources' && sources && (
          <div className="admin-table">
            <h2>News Sources ({sources.sources.length})</h2>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Region</th>
                  <th>Articles</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sources.sources.map(s => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.region}</td>
                    <td>{s.article_count}</td>
                    <td><span className={`status-badge ${s.active ? 'active' : 'inactive'}`}>
                      {s.active ? 'Active' : 'Disabled'}
                    </span></td>
                    <td>
                      <button onClick={() => toggleSource(s.id, !s.active)}>
                        {s.active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'payments' && payments && (
          <div className="admin-table">
            <h2>Payments</h2>
            <div className="payment-summary">
              {Object.entries(payments.summary).map(([status, data]) => (
                <div key={status} className="summary-item">
                  <span className={`status-badge ${status}`}>{status}</span>
                  <span>{data.count} - ${data.total}</span>
                </div>
              ))}
            </div>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {payments.payments.map(p => (
                  <tr key={p.session_id}>
                    <td>{p.email}</td>
                    <td>{p.plan}</td>
                    <td>${p.amount}</td>
                    <td><span className={`status-badge ${p.status}`}>{p.status}</span></td>
                    <td>{new Date(p.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'pipeline' && pipelineStatus && (
          <div className="pipeline-panel">
            <h2>Pipeline Status</h2>
            <div className="pipeline-stats">
              <div className="stat-item">
                <span className="label">Total Articles</span>
                <span className="value">{pipelineStatus.stats.total_articles}</span>
              </div>
              <div className="stat-item">
                <span className="label">With Embeddings</span>
                <span className="value">{pipelineStatus.stats.articles_with_embeddings}</span>
              </div>
              <div className="stat-item">
                <span className="label">Clustered</span>
                <span className="value">{pipelineStatus.stats.articles_clustered}</span>
              </div>
              <div className="stat-item">
                <span className="label">Pending Embeddings</span>
                <span className="value">{pipelineStatus.pending_embeddings}</span>
              </div>
              <div className="stat-item">
                <span className="label">Pending Clustering</span>
                <span className="value">{pipelineStatus.pending_clustering}</span>
              </div>
              <div className="stat-item">
                <span className="label">Last Ingestion</span>
                <span className="value">{pipelineStatus.last_ingestion ? new Date(pipelineStatus.last_ingestion).toLocaleString() : 'Never'}</span>
              </div>
            </div>
            <button className="action-btn primary" onClick={runPipeline}>
              Run Pipeline Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Page Wrapper
const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));

  const handleLogin = (newToken) => {
    localStorage.setItem('adminToken', newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
  };

  return token ? (
    <AdminDashboard token={token} onLogout={handleLogout} />
  ) : (
    <AdminLogin onLogin={handleLogin} />
  );
};

export default AdminPage;
